/**
 * @file PopupButton.ts
 * @role Shared modal action control with primary, secondary, icon-like, and danger variants.
 * @owns Stable hit bounds, visual interaction feedback, enabled/selected state, and callback.
 * @reads Injected visual theme and optional texture.
 * @neverDoes Decide game flow. Persist state. Load assets.
 * @invariants 8, 12.
 */
import { Container, Graphics, Rectangle, Sprite, Text, type Texture } from 'pixi.js';
import type { UiThemeConfig } from '@core/config/UiConfig';

export type PopupButtonVariant = 'primary' | 'secondary' | 'danger';

export interface PopupButtonOptions {
  label: string;
  width?: number;
  height?: number;
  variant?: PopupButtonVariant;
  texture?: Texture;
  accessibleTitle?: string;
  tabIndex?: number;
  fontSize?: number;
  theme?: Pick<
    UiThemeConfig,
    'surface' | 'border' | 'textPrimary' | 'accent' | 'danger' | 'status'
  >;
}

const DEFAULT_THEME = {
  surface: 0x14141f,
  border: 0x2a2a3d,
  textPrimary: 0xe8e8f0,
  accent: 0xf5d76e,
  danger: 0xff7a6e,
  status: 0x12c9c1,
};

export class PopupButton extends Container {
  readonly contentLayer = new Container();
  private readonly background = new Graphics();
  private readonly icon = new Sprite();
  private readonly labelText: Text;
  private handler: (() => void) | undefined;
  private activationHandler: (() => void) | undefined;
  private enabled = true;
  private selected = false;
  private accessibilityEnabled = true;
  private buttonWidth: number;
  private buttonHeight: number;
  private readonly variant: PopupButtonVariant;
  private readonly theme;

  constructor(options: PopupButtonOptions) {
    super();
    this.buttonWidth = options.width ?? 160;
    this.buttonHeight = options.height ?? 48;
    this.variant = options.variant ?? 'secondary';
    this.theme = options.theme ?? DEFAULT_THEME;

    this.icon.anchor.set(0.5);
    this.icon.visible = Boolean(options.texture);
    if (options.texture) this.icon.texture = options.texture;
    this.labelText = new Text({
      text: options.label,
      style: {
        fill: this.theme.textPrimary,
        fontSize: options.fontSize ?? 17,
        fontWeight: '700',
        letterSpacing: 0.4,
      },
    });
    this.labelText.anchor.set(0.5);
    this.contentLayer.addChild(this.background, this.icon, this.labelText);
    this.addChild(this.contentLayer);

    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.accessible = true;
    this.accessibleTitle = options.accessibleTitle ?? options.label;
    this.tabIndex = options.tabIndex ?? 0;
    this.on('pointertap', () => {
      if (!this.enabled || !this.handler) return;
      this.activationHandler?.();
      this.handler();
    });
    this.on('pointerdown', () => this.setPressed(true));
    this.on('pointerup', () => this.setPressed(false));
    this.on('pointerupoutside', () => this.setPressed(false));
    this.on('pointerout', () => this.setPressed(false));
    this.layout(this.buttonWidth, this.buttonHeight);
  }

  onTap(handler: () => void): this {
    this.handler = handler;
    return this;
  }

  onActivated(handler: () => void): this {
    this.activationHandler = handler;
    return this;
  }

  layout(width: number, height = this.buttonHeight): void {
    this.buttonWidth = width;
    this.buttonHeight = height;
    this.hitArea = new Rectangle(-width / 2, -height / 2, width, height);
    this.icon.width = Math.min(24, height * 0.5);
    this.icon.height = Math.min(24, height * 0.5);
    this.redraw();
  }

  setLabel(label: string): void {
    this.labelText.text = label;
    this.accessibleTitle = label;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.eventMode = enabled ? 'static' : 'none';
    this.cursor = enabled ? 'pointer' : 'default';
    this.alpha = enabled ? 1 : 0.42;
    this.setPressed(false);
    this.syncAccessibility();
  }

  setSelected(selected: boolean): void {
    this.selected = selected;
    this.redraw();
  }

  setAccessibilityEnabled(enabled: boolean): void {
    this.accessibilityEnabled = enabled;
    this.syncAccessibility();
  }

  reset(): void {
    this.setPressed(false);
    this.alpha = this.enabled ? 1 : 0.42;
  }

  private redraw(): void {
    const fill =
      this.variant === 'primary'
        ? this.theme.accent
        : this.variant === 'danger'
          ? this.theme.danger
          : this.selected
            ? this.theme.status
            : this.theme.surface;
    const textColor =
      this.variant === 'primary' || this.variant === 'danger' || this.selected
        ? 0x0a0a12
        : this.theme.textPrimary;
    const radius = Math.min(14, this.buttonHeight / 2);
    this.background
      .clear()
      .roundRect(
        -this.buttonWidth / 2,
        -this.buttonHeight / 2,
        this.buttonWidth,
        this.buttonHeight,
        radius,
      )
      .fill({ color: fill, alpha: this.variant === 'secondary' && !this.selected ? 0.94 : 1 })
      .roundRect(
        -this.buttonWidth / 2,
        -this.buttonHeight / 2,
        this.buttonWidth,
        this.buttonHeight,
        radius,
      )
      .stroke({
        width: 2,
        color: this.selected ? this.theme.status : this.theme.border,
      });
    this.labelText.style.fill = textColor;
    const hasIcon = this.icon.visible;
    this.icon.position.set(hasIcon ? -this.buttonWidth * 0.3 : 0, 0);
    this.labelText.position.set(hasIcon ? 10 : 0, 0);
  }

  private setPressed(pressed: boolean): void {
    this.contentLayer.scale.set(pressed && this.enabled ? 0.97 : 1);
  }

  private syncAccessibility(): void {
    this.accessible = this.enabled && this.accessibilityEnabled && this.visible;
  }
}
